import React, { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Box, Typography } from '@mui/material'
import PageWrapper from 'Pages/@PageWrapper'
import { AppFilterPanel, AppPageTabs, AppInfoButton } from '@components'
import { dsColorNew } from '@palette'
import { setupScopeSx } from 'Pages/DsRepricing/Setup/@components/setupScopeSx'
import { DS_REPRICING_ROUTES } from '@constants/routes'

import CategoryConsistencyTab from 'Pages/DsRepricing/Setup/CategoryConsistency'
import PriceLimitsTab         from 'Pages/DsRepricing/Setup/PriceLimits'
import PriceMatricesTab       from 'Pages/DsRepricing/Setup/PriceMatrices'
import ModeSwitch             from 'Pages/DsRepricing/Setup/@components/ModeSwitch'
import QuarantineCard         from 'Pages/DsRepricing/Setup/@components/QuarantineCard'
import RateCardsTab           from 'Pages/DsRepricing/Setup/RateCards'
import TetheringTab           from 'Pages/DsRepricing/Setup/Tethering'
import { PricelistCatalogProvider, AxisCatalogProvider } from 'Pages/DsRepricing/Setup/@services'

/**
 * Repricing General Setup — envelope that constrains every Strategy.
 * Tabs persist through webback (`/api/repricing/setup/*`) by default, or
 * through the Phase-1 localStorage mocks when `dsr_setup:use_mocks` is set
 * (see `@services/flag.js`). Every Surface bypasses the global grey-Paper
 * override to keep contrast readable.
 */
//: Two groups, named at last. The order was already 3 + 3 and
//: contiguous, defended by the two comments below, but nothing on
//: screen said what the halves were (Curro, 2026-10-03: "el setup
//: empieza a ser un monstruo"). Labels only — a second level of
//: navigation would cost a click on every visit and only starts paying
//: past eight or nine leaves.
const GROUPS = [
	{ key: 'sets',   label: 'What sets a floor',
	  keys: ['category-consistency', 'price-matrix', 'price-limits'] },
	{ key: 'engine', label: 'What the engine does with it',
	  keys: ['rate-cards', 'tethering', 'quarantine'] },
]

const TABS = [
	{ key: 'category-consistency', label: 'Category consistency', Component: CategoryConsistencyTab },
	// Before Price limits, because it is written first: a matrix has no
	// scope of its own, and the tab that binds one to a scope is the
	// next one along (Curro, 2026-10-01).
	{ key: 'price-matrix',         label: 'Price matrix',         Component: PriceMatricesTab },
	{ key: 'price-limits',         label: 'Price limits',         Component: PriceLimitsTab },
	{ key: 'rate-cards',           label: 'Rate cards',           Component: RateCardsTab },
	{ key: 'tethering',            label: 'Tethering',            Component: TetheringTab },
	// Last: the last net before a price is sent. A tab and not a header
	// card because the header is for what governs every tab (Curro, 2026-10-01).
	{ key: 'quarantine',           label: 'Quarantine',           Component: QuarantineCard },
]

export default function DsRepricingSetupPage() {
	const [searchParams, setSearchParams] = useSearchParams()

	const initialTab = useMemo(() => {
		const t = searchParams.get('tab')
		return TABS.some(x => x.key === t) ? t : TABS[0].key
	}, [])
	const [activeKey, setActiveKey] = useState(initialTab)

	const setTab = (key) => {
		setActiveKey(key)
		const next = new URLSearchParams(searchParams)
		next.set('tab', key)
		setSearchParams(next, { replace: true })
	}

	const active = TABS.find(t => t.key === activeKey) || TABS[0]
	const Body = active.Component

	return (
		<PricelistCatalogProvider>
		{/* Two tabs need the three face axes — Rate Cards defines them and
		    Price Limits scopes by them — so the catalogue is fetched once
		    here rather than per tab. */}
		<AxisCatalogProvider>
		<PageWrapper
			title={DS_REPRICING_ROUTES.SETUP_TITLE}
			// What Setup is, behind an ⓘ beside the subtitle (it opened the page as a paragraph)
			subtitle={<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
				{DS_REPRICING_ROUTES.SETUP_SUBTITLE}
				<AppInfoButton title={DS_REPRICING_ROUTES.SETUP_TITLE} size={18}>
					The envelope every Strategy runs inside. Anything defined here
					is a hard guardrail — Strategies can never violate it. Use
					Category consistency and Price limits to draw the safety net,
					Rate cards to wire it to the PMS, and Blocked repricings to
					review what the net catches.
				</AppInfoButton>
			</Box>}
		>
			{/* The page's white band, as My Business Admin: the mode that governs every tab, and the
			    tabs in their two groups. */}
			<AppFilterPanel>
				{/* Above the tabs on purpose: it governs every one of them. */}
				<ModeSwitch/>

				<Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 3, flexWrap: 'wrap' }}>
					{GROUPS.map(g => (
						<Box key={g.key} sx={{ minWidth: 0 }}>
							{/* The label sits over its own strip rather than over a
							    slice of one, so it lines up by construction. */}
							<Typography sx={{ mb: 0.75, fontSize: 11, fontWeight: 600, letterSpacing: 0.6,
							                  textTransform: 'uppercase', color: dsColorNew.textSubtle,
							                  whiteSpace: 'nowrap' }}>
								{g.label}
							</Typography>
							<AppPageTabs value={g.keys.includes(activeKey) ? activeKey : false}
							             onChange={(_, k) => setTab(k)}
							             stackProps={{ sx: { mb: 0 } }}
							             sx={{ '& .MuiTabs-flexContainer > .MuiTab-root': { mr: 0 } }}
							             tabs={TABS.filter(t => g.keys.includes(t.key))
							                       .map(t => ({ key: t.key, value: t.key, label: t.label }))}/>
						</Box>
					))}
				</Box>
			</AppFilterPanel>

			<Box sx={setupScopeSx}>
				<Body/>
			</Box>
		</PageWrapper>
		</AxisCatalogProvider>
		</PricelistCatalogProvider>
	)
}
